/**
 * MinimalDecisionView.jsx
 * V5.0: Investor-First Minimal Decision View
 * 
 * Design Principle: Decision-first, data-second
 * Goal: 5-second clarity - user understands deal instantly
 * 
 * SHOWS ONLY:
 * 1. Deal Decision (BUY/NEGOTIATE/PASS) - large, centered
 * 2. 4 Core Metrics: Net Profit, Profit Margin, ARV, Target Offer
 * 3. Short Explanation (MAX 2 lines)
 * 4. ONE Action Suggestion
 * 5. "View Details" button to expand full analysis
 * 
 * P0 FIX: Now receives standardized decision values from getCanonicalDecision()
 * Expected values: 'GOOD_DEAL', 'MARGINAL', 'NO_PASS'
 */

import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, ChevronDown, DollarSign, Percent, Home, Target, TrendingUp } from 'lucide-react';
import { financialTextColor, tierTextColor } from '../utils/financialColor';

const MinimalDecisionView = ({ 
  decision,
  decisionLabel,  // P0 FIX: Strategy-aware label from getCanonicalDecision()
  netProfit,
  profitMargin,
  arv,
  targetOffer,
  targetOfferViable = true,  // false => no purchase price works; show "—", never $1
  purchasePrice,
  arvBlocked = false,  // PROBLEM 2: ARV honest-blocked -> no confident derived numbers
  blockedReason = 'add_comps',  // E: 'confirm_type' when the unit count is unconfirmed
  shortExplanation,
  actionSuggestion,
  onViewDetails,
  showDetails,
  lang = 'en',
  onToggleFinancing,
  financingMode = 'cash',
  isRecalculating = false,
  strategy = 'flip',
  rentalCashflow = 0,
  rentalDscr = 0,
  rentalCapRate = 0,
  purchaseFinancing = 'cash',
  rehabFinancing = purchaseFinancing,   // the other leg; defaults to "same as purchase"
  monthlyIOPayment = 0,
  rentalFinancingType = 'cash',
  monthlyDebtService = 0,   // P+I only — what cash flow and DSCR use
  monthlyPiti = 0,          // P+I + taxes + insurance — what the owner pays
  monthlyEscrow = 0,        // taxes + insurance alone — the cash-mode carrying cost
  // A cash-out REFINANCE has no purchase: the price tile becomes the appraised value.
  isRefinance = false,
}) => {
  // P0 FIX: Now uses decisionLabel passed from getCanonicalDecision() for strategy-aware labels
  // Labels: Flip = BUY/NEGOTIATE/PASS | Rental = STRONG RENTAL/MODERATE RENTAL/WEAK RENTAL
  const getDecisionConfig = () => {
    // Standardized decision values: 'GOOD_DEAL', 'MARGINAL', 'NO_PASS'
    if (decision === 'GOOD_DEAL') {
      return {
        label: decisionLabel || (lang === 'en' ? 'BUY' : 'COMPRAR'),
        bgColor: 'bg-emerald-500',
        textColor: 'text-white',
        icon: CheckCircle,
        borderColor: 'border-emerald-500'
      };
    } else if (decision === 'MARGINAL') {
      return {
        label: decisionLabel || (lang === 'en' ? 'NEGOTIATE' : 'NEGOCIAR'),
        bgColor: 'bg-amber-500',
        textColor: 'text-white',
        icon: AlertTriangle,
        borderColor: 'border-amber-500'
      };
    } else {
      // NO_PASS or fallback — AVOID, never the ambiguous "PASS".
      return {
        label: decisionLabel || (lang === 'en' ? 'AVOID' : 'EVITAR'),
        bgColor: 'bg-red-500',
        textColor: 'text-white',
        icon: XCircle,
        borderColor: 'border-red-500'
      };
    }
  };

  const config = getDecisionConfig();
  const Icon = config.icon;

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0';
    // PROBLEM 1: strip $/commas so a formatted string ("1,500,000") isn't truncated to 1
    // by parseFloat stopping at the first comma.
    const num = typeof value === 'string' ? parseFloat(value.replace(/[$,\s]/g, '')) : value;
    if (isNaN(num)) return '$0';
    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    return `${sign}$${Math.round(absNum).toLocaleString()}`;
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0%';
    const num = typeof value === 'string' ? parseFloat(String(value).replace(/[%,\s]/g, '')) : value;
    return `${(isNaN(num) ? 0 : num).toFixed(1)}%`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-slate-100 overflow-hidden" data-testid="minimal-decision-view">
      {/* SECTION 1: Decision Badge - THE HERO */}
      <div className={`${config.bgColor} px-6 py-8 text-center`}>
        <div className="flex items-center justify-center gap-3 mb-2">
          <Icon className="w-10 h-10 text-white" />
          <span className={`text-5xl sm:text-6xl font-black ${config.textColor} tracking-tight`}>
            {config.label}
          </span>
        </div>
        
        {/* Short Explanation - MAX 2 lines */}
        {shortExplanation && (
          <p className="text-white/90 text-base sm:text-lg max-w-lg mx-auto mt-3 line-clamp-2">
            {shortExplanation}
          </p>
        )}
      </div>

      {/* SECTION 2: 4 Core Metrics - Strategy Aware */}
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100">
        {strategy === 'rental' ? (
          <>
            {/* Rental: Cash Flow/mo */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <TrendingUp className="w-3.5 h-3.5" />
                {lang === 'en' ? 'Cash Flow/mo' : 'Flujo/mes'}
              </div>
              <div className={`text-xl sm:text-2xl font-bold ${financialTextColor(rentalCashflow)}`}>
                {formatCurrency(rentalCashflow)}
              </div>
            </div>

            {/* Rental: DSCR */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <Percent className="w-3.5 h-3.5" />
                {lang === 'en' ? 'DSCR' : 'DSCR'}
              </div>
              {/* DSCR = NOI / debt service. With no loan there is no debt service, so the
                  ratio is undefined — NOT zero. Rendering "0.00x" in red read as a deal
                  that fails to cover its debt, when there is no debt to cover. */}
              {monthlyDebtService > 0 ? (
                <div className={`text-xl sm:text-2xl font-bold ${rentalDscr >= 1.25 ? 'text-emerald-600' : rentalDscr >= 1.0 ? 'text-amber-600' : 'text-red-600'}`}
                     data-testid="rental-dscr-value">
                  {rentalDscr.toFixed(2)}x
                </div>
              ) : (
                <>
                  <div className="text-xl sm:text-2xl font-bold text-slate-400" data-testid="rental-dscr-na">
                    {lang === 'en' ? 'N/A' : 'N/A'}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {lang === 'en' ? 'no loan — cash purchase' : 'no aplica — compra en efectivo'}
                  </div>
                </>
              )}
            </div>

            {/* Rental: Cap Rate */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <Home className="w-3.5 h-3.5" />
                {lang === 'en' ? 'Cap Rate' : 'Tasa Cap'}
              </div>
              <div className={`text-xl sm:text-2xl font-bold ${tierTextColor(rentalCapRate, 6)}`}>
                {rentalCapRate.toFixed(1)}%
              </div>
            </div>

            {/* Rental: Purchase Price — or, on a REFINANCE, the appraised value.
                You already own a refinanced property; there is no purchase and no
                purchase price. Showing one (twice, alongside "Appraised Value" for the
                same figure) is simply wrong for the scenario. */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <Target className="w-3.5 h-3.5" />
                {isRefinance
                  ? (lang === 'en' ? 'Appraised Value' : 'Valor Tasado')
                  // nosemgrep: purchase-price-must-be-refinance-aware -- isRefinance ternary above
                  : (lang === 'en' ? 'Purchase Price' : 'Precio')}
              </div>
              <div className="text-xl sm:text-2xl font-bold text-slate-700"
                   data-testid={isRefinance ? 'rental-appraised-value' : 'rental-purchase-price'}>
                {formatCurrency(purchasePrice)}
              </div>
            </div>

            {/* Rental: monthly obligation.
                DSCR  -> PITI (P+I + taxes + insurance) — what the owner pays.
                CASH  -> the carrying cost (taxes + insurance). A cash rental has NO
                         loan payment, but it is not free to hold, so the investor
                         still needs the real monthly number. Labelled distinctly so
                         it is never read as a mortgage payment. */}
            {rentalFinancingType !== 'dscr' && monthlyEscrow > 0 && (
              <div className="p-4 sm:p-6 text-center">
                <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  {lang === 'en' ? 'Monthly Carrying Cost' : 'Costo Mensual de Tenencia'}
                </div>
                <div className="text-xl sm:text-2xl font-bold text-slate-700" data-testid="rental-monthly-carrying-cost">
                  {formatCurrency(monthlyEscrow)}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {lang === 'en' ? 'taxes + insurance' : 'impuestos + seguro'}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5" data-testid="rental-carrying-no-loan">
                  {lang === 'en' ? 'no loan — paid in cash' : 'sin préstamo — pagado en efectivo'}
                </div>
              </div>
            )}

            {rentalFinancingType === 'dscr' && (
              <div className="p-4 sm:p-6 text-center">
                <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  {lang === 'en' ? 'Monthly Payment' : 'Pago Mensual'}
                </div>
                {/* Show PITI — what the owner actually pays monthly. P+I alone
                    understates the real obligation. Taxes/insurance are already in
                    operating expenses, so cash flow + DSCR keep using P+I; this tile
                    is display only. */}
                <div className="text-xl sm:text-2xl font-bold text-slate-700" data-testid="rental-monthly-piti">
                  {formatCurrency(monthlyPiti || monthlyDebtService)}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {monthlyPiti
                    ? (lang === 'en' ? 'PITI — principal, interest, taxes, insurance'
                                     : 'PITI — capital, interés, impuestos, seguro')
                    : (lang === 'en' ? 'principal + interest' : 'principal + interés')}
                </div>
                {monthlyPiti > 0 && monthlyDebtService > 0 && (
                  <div className="text-[11px] text-slate-400 mt-0.5" data-testid="rental-piti-split">
                    {formatCurrency(monthlyDebtService)} {lang === 'en' ? 'P&I' : 'C&I'}
                    {' + '}{formatCurrency(monthlyPiti - monthlyDebtService)}{' '}
                    {lang === 'en' ? 'escrow' : 'depósito'}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* PROBLEM 2: when the ARV is honest-blocked, Net Profit / Margin / ARV /
                Target Offer are all derived from an unreliable (as-is) value — show the
                "add renovated comps" state instead of $1 / a huge negative number. */}
            {/* Flip: Net Profit */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <DollarSign className="w-3.5 h-3.5" />
                {lang === 'en' ? 'Net Profit' : 'Ganancia'}
              </div>
              {arvBlocked ? (
                <div className="text-sm sm:text-base font-bold text-amber-700" data-testid="hero-blocked-reason">
                  {blockedReason === 'confirm_type'
                    ? (lang === 'en' ? 'Confirm property type' : 'Confirma el tipo')
                    : (lang === 'en' ? 'Add comps' : 'Agregar comps')}
                </div>
              ) : (
                <div className={`text-xl sm:text-2xl font-bold ${financialTextColor(netProfit)}`}>
                  {formatCurrency(netProfit)}
                </div>
              )}
            </div>

            {/* Flip: Profit Margin */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <Percent className="w-3.5 h-3.5" />
                {lang === 'en' ? 'Margin' : 'Margen'}
              </div>
              {arvBlocked ? (
                <div className="text-xl sm:text-2xl font-bold text-amber-700">—</div>
              ) : (
                <div className={`text-xl sm:text-2xl font-bold ${tierTextColor(profitMargin, 10)}`}>
                  {formatPercent(profitMargin)}
                </div>
              )}
            </div>

            {/* Flip: ARV */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <Home className="w-3.5 h-3.5" />
                {lang === 'en' ? 'ARV' : 'ARV'}
              </div>
              {/* Value, badge, what to do — three pieces, and the SAME number the detail
                  card shows. This tile used to render only "Add renovated comps", so the
                  hero and the card on the same page disagreed about whether a figure
                  existed at all. The fourth piece (why THIS ARV is weak, per tier) is
                  deliberately NOT here: at four lines the tile dwarfed its three
                  neighbours in the metrics row. It lives in the detail card below. */}
              {arvBlocked ? (
                <>
                  {arv > 0 && (
                    <div className="text-xl sm:text-2xl font-bold text-amber-800" data-testid="hero-arv-amount">
                      {formatCurrency(arv)}
                    </div>
                  )}
                  <div className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">
                    {blockedReason === 'confirm_type'
                      ? (lang === 'en' ? 'Property type not confirmed' : 'Tipo de propiedad sin confirmar')
                      : (lang === 'en' ? 'Low confidence — limited comps' : 'Baja confianza — comps limitados')}
                  </div>
                  <div className="text-[11px] font-semibold text-amber-700 mt-1 leading-snug">
                    {lang === 'en'
                      ? (blockedReason === 'confirm_type' ? 'Confirm the type to compute the value' : 'Add your comps for a stronger ARV')
                      : 'Agrega tus comps para un ARV más preciso'}
                  </div>
                </>
              ) : (
                <div className="text-xl sm:text-2xl font-bold text-slate-700">
                  {formatCurrency(arv)}
                </div>
              )}
            </div>

            {/* Flip: Target Offer */}
            <div className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                <Target className="w-3.5 h-3.5" />
                {lang === 'en' ? 'Target Offer' : 'Oferta Objetivo'}
              </div>
              {arvBlocked || !targetOfferViable ? (
                <div className="text-xl sm:text-2xl font-bold text-amber-700" data-testid="target-offer-not-viable">—</div>
              ) : (
                <div className="text-xl sm:text-2xl font-bold text-slate-700">
                  {formatCurrency(targetOffer)}
                </div>
              )}
            </div>

            {/* Flip: Monthly I/O Payment — whenever EITHER leg carries a loan. It was gated
                on the purchase alone, so "purchase cash, rehab financed" hid a real
                $1,056/mo of interest. monthlyIOPayment is the backend's sum of both legs. */}
            {(purchaseFinancing === 'financed' || rehabFinancing === 'financed') && monthlyIOPayment > 0 && (
              <div className="p-4 sm:p-6 text-center">
                <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs sm:text-sm mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  {lang === 'en' ? 'Monthly Payment' : 'Pago Mensual'}
                </div>
                <div className="text-xl sm:text-2xl font-bold text-slate-700" data-testid="flip-monthly-payment">
                  {formatCurrency(monthlyIOPayment)}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {lang === 'en' ? 'interest only' : 'solo interés'}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* SECTION 3: Action Suggestion - ONE clear action */}
      {actionSuggestion && (
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
          <div className="flex items-start gap-2">
            <span className="text-lg">💡</span>
            <p className="text-sm sm:text-base text-slate-700 font-medium">
              {actionSuggestion}
            </p>
          </div>
        </div>
      )}

      {/* SECTION 4: Quick Actions Bar */}
      <div className="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Financing Toggle - Compact */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{lang === 'en' ? 'Mode:' : 'Modo:'}</span>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => onToggleFinancing?.('cash')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                financingMode === 'cash' 
                  ? 'bg-white text-emerald-700 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              disabled={isRecalculating}
            >
              {lang === 'en' ? 'Cash' : 'Efectivo'}
            </button>
            <button
              onClick={() => onToggleFinancing?.('financed')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                financingMode === 'financed' 
                  ? 'bg-white text-blue-700 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              disabled={isRecalculating}
            >
              {lang === 'en' ? 'Financed' : 'Financiado'}
            </button>
          </div>
          {/* The two buttons drive the PURCHASE leg. When the rehab leg differs, say so —
              otherwise "Cash" reads as "no loan" over a deal that is paying interest. */}
          {strategy !== 'rental' && purchaseFinancing !== rehabFinancing && (
            <span
              className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5"
              data-testid="financing-mode-mixed"
            >
              {lang === 'en'
                ? `Mixed · rehab ${rehabFinancing === 'financed' ? 'financed' : 'cash'}`
                : `Mixto · rehab ${rehabFinancing === 'financed' ? 'financiado' : 'en efectivo'}`}
            </span>
          )}
          {isRecalculating && (
            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          )}
        </div>

        {/* View Details Button */}
        <button
          onClick={onViewDetails}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all"
          data-testid="view-details-btn"
        >
          {lang === 'en' ? 'View Full Analysis' : 'Ver Análisis Completo'}
          <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default MinimalDecisionView;
